import React, { RefObject, useContext, useRef, useState, useEffect } from 'react';

import { PawCircle } from '@zooplus/icons';
import { ButtonAnchor } from '@zooplus/ui-components';

import gradient from '../assets/img/gradient.png';
import zooclubPoints from '../assets/img/zooclubPoints.svg';
import zooPoints from '../assets/img/zooPoints.svg';
import LocResourcesContext from '../contexts/LocResourcesContext';
import { sendEventToAdobe } from '../utils/adobeUtils';
import Cookie from '../utils/cookies';
import { BonusPointsOverview } from '../utils/types';

interface CustomerBalanceProps {
  redeemableBalance: number;
  bonusPointsOverview: BonusPointsOverview;
  hasAnyRedemption: boolean;
  showRewardShopButton: boolean;
  isRewardShopPage: boolean;
  hideExpiryDate: boolean;
  loyaltyProgramEnabled?: boolean;
}

const CustomerBalanceComponent = ({
  redeemableBalance,
  bonusPointsOverview,
  hasAnyRedemption,
  showRewardShopButton,
  isRewardShopPage,
  hideExpiryDate,
  loyaltyProgramEnabled,
}: CustomerBalanceProps): JSX.Element => {
  const boxRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const hasExpiringBalance =
    !!bonusPointsOverview.expiryDate && !!bonusPointsOverview.expiringBalance;
  const [barMode, setBarMode] = useState<boolean>(false);
  const [firstExpiringDate] = useState<Date | undefined>(
    hasExpiringBalance ? new Date(bonusPointsOverview.expiryDate) : undefined,
  );
  const [firstExpiringBalance] = useState<number | undefined>(
    hasExpiringBalance ? bonusPointsOverview.expiringBalance : undefined,
  );
  const { onFormatLocale, onFormatDate } = useContext(LocResourcesContext);

  const getBarModeClass = () => {
    return barMode ? 'bar-mode' : '';
  };

  const checkBoxVisibility = () => {
    setBarMode(!isElementVisible(boxRef.current));
  };

  useEffect(() => {
    const handleScroll = () => {
      checkBoxVisibility();
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isElementVisible = (element: any) => {
    return element.getBoundingClientRect().top + getDummyBoxHeight() >= 0;
  };

  const getDummyBoxHeight = () => {
    const isMobile = (window.innerWidth || document.documentElement.clientWidth) <= 543;
    return isMobile ? 110 : 80;
  };

  const isMobileApp = () => {
    const isMobile = Cookie.get('MOBILE_APP');
    return isMobile !== undefined;
  };

  const customerBalance = () => {
    const showWhatAreZoopointsButton = !hasAnyRedemption && !showRewardShopButton;
    const hasExpiring = firstExpiringDate && firstExpiringBalance && firstExpiringBalance > 0;

    const lastExpiring = hasExpiring ? (
      <React.Fragment>
        <span className="loy-bpo-balance-v2_section_header">
          {onFormatLocale?.('loy.points.expiry') + ' '}
          <span>{onFormatDate?.(firstExpiringDate)}</span>
        </span>
        <div
          className={
            'loy-bpo-balance-v2_section_value' + (loyaltyProgramEnabled ? ' loyaltyProgram' : '')
          }>
          {firstExpiringBalance}
        </div>
      </React.Fragment>
    ) : (
      ''
    );

    const whatAreZoopointsButton = (
      <ButtonAnchor
        href="/account/bonuspoints/overview#loy-bppo-what-are-zoopoints"
        id="loy-bppo-what-are-zoopoints"
        variant="negative"
        onClick={() => sendEventToAdobe('bpFilter', 'event552')}>
        {onFormatLocale?.('loy.bp.what-are-zoopoints')}
      </ButtonAnchor>
    );

    const rewardShopButton = (
      <ButtonAnchor
        href="/account/bonuspoints/products/overview"
        id="loy-bppo-what-are-zoopoints"
        onClick={() =>
          sendEventToAdobe(
            'clickTrack',
            '^tm:Loyalty^trk:Bonus Points^val:Account Page / Reward Shop Button',
          )
        }>
        {onFormatLocale?.('loy.rewardshop.link')}
      </ButtonAnchor>
    );

    const hideBox = (barMode && isRewardShopPage) || !redeemableBalance;

    return (
      <>
        <div
          ref={boxRef}
          className={
            'loy-bpo-balance-v2_container_dummy ' +
            getBarModeClass() +
            (isRewardShopPage ? '' : ' hidden') +
            (' height' + getDummyBoxHeight())
          }
        />
        <div
          className={
            'loy-bpo-balance-v2_container bar-mode ' +
            (barMode && redeemableBalance && isRewardShopPage ? '' : 'hidden') +
            (isMobileApp() ? ' top0' : '') +
            (loyaltyProgramEnabled ? ' loyaltyProgram' : '')
          }>
          {loyaltyProgramEnabled ? <img src={zooclubPoints} alt="" /> : <PawCircle aria-hidden />}
          <span className="text">{onFormatLocale?.('loy.points.available')}</span>
          <span className="balance">
            {redeemableBalance + ' ' + onFormatLocale?.('mzp.points.points')}
          </span>
        </div>
        <div
          className={
            'loy-bpo-balance-v2 loy-bpo-balance-v2_container ' +
            (hideBox ? 'hidden' : '') +
            (loyaltyProgramEnabled ? ' loyaltyProgram' : '')
          }>
          <div className="loy-bpo-balance-v2_section">
            <div className="loy-bpo-balance-v2_points_container">
              <div className="loy-bpo-balance-v2_points_data">
                <span className="loy-bpo-balance-v2_section_header">
                  {onFormatLocale?.('loy.points.available')}
                </span>
                <div
                  className={
                    'loy-bpo-balance-v2_section_value' +
                    (loyaltyProgramEnabled ? ' loyaltyProgram' : '')
                  }>
                  {redeemableBalance}
                </div>
              </div>
              {!hideExpiryDate && (
                <div className="loy-bpo-balance-v2_points_data">{lastExpiring}</div>
              )}
            </div>
          </div>
          <div className="loy-bpo-balance-v2_section_cta">
            {showRewardShopButton && rewardShopButton}
            {showWhatAreZoopointsButton && whatAreZoopointsButton}
            <div className="loy-bpo-balance-v2__decoration" aria-hidden>
              {loyaltyProgramEnabled ? (
                <>
                  <img
                    src={zooclubPoints}
                    className="loy-bpo-balance-v2__decoration--big-icon"
                    alt=""
                  />
                  <img
                    src={zooclubPoints}
                    className="loy-bpo-balance-v2__decoration--small-icon"
                    alt=""
                  />
                </>
              ) : (
                <>
                  <img
                    src={zooPoints}
                    className="loy-bpo-balance-v2__decoration--big-icon"
                    alt=""
                  />
                  <img
                    src={zooPoints}
                    className="loy-bpo-balance-v2__decoration--small-icon"
                    alt=""
                  />
                  <img className="loy-bpo-balance-v2__decoration--gradient" src={gradient} alt="" />
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  return customerBalance();
};

export default CustomerBalanceComponent;
